import { MetaTagFragment } from '@custom/schema';
import React from 'react';

import { buildUrl } from './build-url';
import { isRelativeUrl } from './is-relative-url';
import { transformMetatagToHeadProps } from './transform-metatags';

export type PageHeadProps = {
  metaTags?: MetaTagFragment[];
  schemaOrg?: string;
};

export function PageHead({ metaTags, schemaOrg }: PageHeadProps) {
  const { title, links, meta } = transformMetatagToHeadProps(metaTags);
  const domain = 'https://www.knime.com';

  const twitterImage = meta
    ? meta.filter((item) => item.name === 'twitter:image')
    : [];
  const ogImage = meta ? meta.filter((item) => item.name === 'og:image') : [];
  const ogImageUrl = meta
    ? meta.filter((item) => item.name === 'og:image:url')
    : [];
  const ogImageSecureUrl = meta
    ? meta.filter((item) => item.name === 'og:image:secure_url')
    : [];

  // Use the first available image with this order
  const ogImageReplacement = ogImage.length
    ? ogImage
    : ogImageUrl.length
    ? ogImageUrl
    : ogImageSecureUrl.length
    ? ogImageSecureUrl
    : [];

  return (
    <>
      <html lang="en" />
      <title>{title}</title>
      {links &&
        links
          .map((link) => {
            if (!['shortlink', 'canonical'].includes(link.rel)) {
              return link;
            }

            return {
              ...link,
              href: isRelativeUrl(link.href)
                ? buildUrl([domain, ...link.href.split('/')])
                : link.href,
            };
          })
          .map(({ rel, href }, index) => (
            <link key={`link-${index}`} rel={rel} href={href} />
          ))}
      {
        meta &&
          meta
            .map((item) => {
              // Handle og:url separately as it needs the domain prepended if relative
              if (
                item.property === 'og:url' &&
                typeof item.content === 'string'
              ) {
                return {
                  ...item,
                  content: isRelativeUrl(item.content)
                    ? buildUrl([domain, ...item.content.split('/')])
                    : item.content,
                };
              }
              return item; // Return item as is if not og:url
            })
            .map((item, index) => {
              // Render the meta tag using either name or property
              const attributes = item.name
                ? { name: item.name, content: item.content }
                : item.property
                ? { property: item.property, content: item.content }
                : null;

              if (!attributes) {
                return null; // Skip if neither name nor property is present
              }

              return <meta key={`meta-${index}`} {...attributes} />;
            })
            .filter(Boolean) // Remove any null returns from the map
      }

      {ogImageReplacement.map((item) => {
        if (twitterImage.length === 0) {
          return (
            <meta
              key={'twitter-image'}
              name="twitter:image"
              content={item.content}
            />
          );
        }
      })}

      <meta httpEquiv="Content-Language" content={'en-US'} />
      <meta name="viewport" content="width=device-width" />
      {schemaOrg && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaOrg }}
        />
      )}
    </>
  );
}
